/**
 * Detail for one bulk RMA action request: summary counts and chunk ledger.
 * Opens/closes the returns list store alongside so the detail view has one entry point.
 */
// eslint-disable-next-line
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { BULK_REQUEST_STATUS } from '../../../../../services/api/bulk-rma-action-types';
import { rootContainer, TYPE } from '../../../../../services/container/root';
import TroubleshootingService, {
    IBulkRmaActionRequestDetail,
} from '../../../../../services/api/troubleshooting-service';
import bulkRmaActionRequestsPagedListStore from './bulk-rma-action-requests-paged-list-store';
import bulkRmaActionReturnsPagedListStore from './bulk-rma-action-returns-paged-list-store';

const TERMINAL_REQUEST_MESSAGE = 'Request is already finished or cancelled.';

export class BulkRmaActionRequestDetailStore {
    @observable storeId: string | number | null = null;
    @observable requestId: number | null = null;
    @observable detail: IBulkRmaActionRequestDetail | null = null;
    @observable isLoading: boolean = false;
    @observable error: string | null = null;
    @observable isCancelling: boolean = false;
    @observable cancelError: string | null = null;

    constructor() {
        makeObservable(this);
    }

    private get troubleshootingService(): TroubleshootingService {
        return rootContainer.get<TroubleshootingService>(TYPE.TroubleshootingService);
    }

    get isOpen(): boolean {
        return this.storeId != null && this.requestId != null;
    }

    @computed
    get canCancelRequest(): boolean {
        if (!this.detail) {
            return false;
        }
        return (
            this.detail.status === BULK_REQUEST_STATUS.PROCESSING
            && !this.detail.cancel_requested
        );
    }

    @computed
    get isCancellationInProgress(): boolean {
        if (!this.detail) {
            return false;
        }
        return (
            this.detail.status === BULK_REQUEST_STATUS.PROCESSING
            && this.detail.cancel_requested
        );
    }

    @action
    clearCancelError(): void {
        this.cancelError = null;
    }

    @action
    async open(storeId: string | number, requestId: number): Promise<void> {
        this.storeId = storeId;
        this.requestId = requestId;
        this.detail = null;
        this.error = null;
        this.cancelError = null;
        this.isLoading = true;

        try {
            const detail = await this.troubleshootingService.getBulkRmaActionRequestDetail(
                storeId,
                requestId,
            );
            runInAction(() => {
                this.detail = detail;
                this.error = null;
            });
            await bulkRmaActionReturnsPagedListStore.open(storeId, requestId);
        } catch (err: any) {
            runInAction(() => {
                this.detail = null;
                this.error = err?.response?.data?.error ?? err?.message ?? 'Failed to load request detail';
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action
    async cancelRequest(): Promise<boolean> {
        if (this.storeId == null || this.requestId == null || !this.canCancelRequest) {
            return false;
        }

        this.isCancelling = true;
        this.cancelError = null;

        try {
            await this.troubleshootingService.cancelBulkRmaActionRequest(
                this.storeId,
                this.requestId,
            );
            await this.open(this.storeId, this.requestId);
            await bulkRmaActionRequestsPagedListStore.loadPage(
                bulkRmaActionRequestsPagedListStore.page,
            );
            return true;
        } catch (err: any) {
            const status = err?.response?.status;
            runInAction(() => {
                if (status === 409) {
                    this.cancelError = TERMINAL_REQUEST_MESSAGE;
                } else {
                    this.cancelError = err?.response?.data?.error
                        ?? err?.message
                        ?? 'Failed to cancel request';
                }
            });
            return false;
        } finally {
            runInAction(() => {
                this.isCancelling = false;
            });
        }
    }

    @action
    close(): void {
        this.storeId = null;
        this.requestId = null;
        this.detail = null;
        this.error = null;
        this.cancelError = null;
        this.isCancelling = false;
        this.isLoading = false;
        bulkRmaActionReturnsPagedListStore.close();
    }
}

export default new BulkRmaActionRequestDetailStore();
